import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { PencilIcon, EyeIcon } from '@heroicons/react/24/outline';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Tip } from './ui/tip';
import { ButtonGroup } from './ui/button-group';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarSeparator,
  MenubarShortcut,
  MenubarTrigger,
} from './ui/menubar';

interface Provider {
  id: string;
  name: string;
  companyName?: string;
  type: 'individual' | 'company';
  email?: string;
  phone?: string;
  mobile?: string;
  website?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  taxId?: string;
  categories: string[] | string | null;
  specialties: string[] | string | null;
  paymentTerms: string;
  paymentMethod?: string;
  avgTermDays?: number;
  discountBenefit?: string;
  creditLimit?: number;
  currentBalance: number;
  contactPerson?: string;
  contactPosition?: string;
  contactPhone?: string;
  contactEmail?: string;
  isActive: boolean;
  isPreferred: boolean;
  rating?: number;
  notes?: string;
  lastOrderDate?: string;
  lastContactDate?: string;
  fullAddress?: string;
  fullName?: string;
  primaryContact?: {
    name: string;
    email?: string;
    phone?: string;
  };
  isOverCreditLimit?: boolean;
  creditUtilization?: number;
  importanceLevel?: 'critico' | 'regular' | 'alternativo' | 'problematico';
  preferredAuto?: boolean;
  preferredScore?: number;
  lastPurchaseDate?: string | null;
  lastMonthTotal?: number;
  purchaseFrequency?: string;
  balanceOverdue?: number;
  balanceCurrent?: number;
  nextDueDate?: string | null;
}

interface ProviderDetailModalProps {
  provider: Provider;
  isOpen: boolean;
  onClose: () => void;
  onEdit?: (provider: Provider) => void;
}

type AccountResponse = {
  account?: {
    id: string;
    currentBalance: number;
    creditLimit?: number;
    updatedAt?: string;
  };
};

type AccountMovement = {
  id: string;
  type: 'charge' | 'payment' | 'credit' | 'debit' | 'adjustment';
  amount: number;
  description: string;
  reference?: string;
  previousBalance: number;
  newBalance: number;
  createdAt: string;
  paymentMethod?: string;
};

type Purchase = {
  id: string;
  purchaseNumber?: string;
  status: string;
  date: string;
  subtotal: number;
  tax: number;
  total: number;
  notes?: string;
  items?: Array<{
    id: string;
    name: string;
    qty: number;
    unitCost: number;
    totalCost: number;
  }>;
};

type MovementsState = {
  loading: boolean;
  error: string | null;
  entries: AccountMovement[];
  accountId: string | null;
  balance: number | null;
  creditLimit?: number | null;
};

type PurchasesState = {
  loading: boolean;
  error: string | null;
  entries: Purchase[];
};

const MOVEMENT_TYPE_LABEL: Record<AccountMovement['type'], string> = {
  charge: 'Compra / Cargo',
  payment: 'Pago',
  credit: 'Nota de crédito',
  debit: 'Débito',
  adjustment: 'Ajuste'
};

const ProviderDetailModal: React.FC<ProviderDetailModalProps> = ({ provider, isOpen, onClose, onEdit }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'contact' | 'ctacte' | 'purchases'>('overview');
  const [selectedPurchase, setSelectedPurchase] = useState<Purchase | null>(null);
  const [movementsState, setMovementsState] = useState<MovementsState>({
    loading: false,
    error: null,
    entries: [],
    accountId: null,
    balance: null,
    creditLimit: null
  });
  const [purchasesState, setPurchasesState] = useState<PurchasesState>({
    loading: false,
    error: null,
    entries: []
  });

  useEffect(() => {
    if (!isOpen || !provider?.id) return;

    let isMounted = true;

    const loadMovements = async () => {
      setMovementsState((prev) => ({
        ...prev,
        loading: true,
        error: null,
        entries: []
      }));

      try {
        const { data: accountData } = await axios.get<AccountResponse>(
          `/api/current-accounts/provider/${provider.id}`
        );

        const accountId = accountData.account?.id ?? null;
        const creditLimit = accountData.account?.creditLimit ?? null;
        const balance = accountData.account?.currentBalance ?? null;

        let entries: AccountMovement[] = [];
        if (accountId) {
          const { data: movementsData } = await axios.get<{ movements: AccountMovement[] }>(
            `/api/current-accounts/${accountId}/movements`,
            { params: { limit: 50 } }
          );
          entries = Array.isArray(movementsData.movements) ? movementsData.movements : [];
        }

        if (!isMounted) return;

        setMovementsState({
          loading: false,
          error: null,
          entries,
          accountId,
          balance,
          creditLimit
        });
      } catch (error: any) {
        if (!isMounted) return;
        setMovementsState({
          loading: false,
          error: error?.response?.data?.error || 'No se pudieron obtener los movimientos',
          entries: [],
          accountId: null,
          balance: null,
          creditLimit: null
        });
      }
    };

    const loadPurchases = async () => {
      setPurchasesState({
        loading: true,
        error: null,
        entries: []
      });

      try {
        const { data } = await axios.get<{ purchases: Purchase[] }>('/api/purchases', {
          params: {
            providerId: provider.id
          }
        });

        if (!isMounted) return;

        setPurchasesState({
          loading: false,
          error: null,
          entries: Array.isArray(data.purchases) ? data.purchases : []
        });
      } catch (error: any) {
        if (!isMounted) return;
        setPurchasesState({
          loading: false,
          error: error?.response?.data?.error || 'No se pudieron obtener las compras',
          entries: []
        });
      }
    };

    setActiveTab('overview');
    loadMovements();
    loadPurchases();

    return () => {
      isMounted = false;
    };
  }, [isOpen, provider?.id]);

  const formattedName = useMemo(() => {
    if (!provider) return '';
    return provider.companyName || provider.name;
  }, [provider]);

  const categories = useMemo(() => {
    if (!provider) return [];
    // Manejar casos donde viene como string o null desde el servidor
    if (Array.isArray(provider.categories)) return provider.categories.filter(Boolean);
    if (typeof provider.categories === 'string' && provider.categories.trim().length > 0) {
      return provider.categories.split(',').map((item: string) => item.trim()).filter(Boolean);
    }
    return [];
  }, [provider]);

  const specialties = useMemo(() => {
    if (!provider) return [];
    // Manejar casos donde viene como string o null desde el servidor
    if (Array.isArray(provider.specialties)) return provider.specialties.filter(Boolean);
    if (typeof provider.specialties === 'string' && provider.specialties.trim().length > 0) {
      return provider.specialties.split(',').map((item: string) => item.trim()).filter(Boolean);
    }
    return [];
  }, [provider]);

  const formatCurrency = (value?: number | null) => {
    if (value === undefined || value === null || Number.isNaN(value)) return '$ 0,00';
    return value.toLocaleString('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 2
    });
  };

  const formatDate = (value?: string | null) => {
    if (!value) return 'Sin datos';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getImportanceLabel = (level?: Provider['importanceLevel']) => {
    switch (level) {
      case 'critico':
        return 'Crítico';
      case 'alternativo':
        return 'Alternativo';
      case 'problematico':
        return 'Problemático';
      default:
        return 'Regular';
    }
  };

  const getPaymentMethodLabel = (providerData: Provider) => {
    const methodMap: Record<string, string> = {
      contado: 'Contado',
      ctacte: 'Cta Cte',
      cta_cte: 'Cta Cte',
      cheque: 'Cheque',
      transferencia: 'Transferencia',
      otro: 'Otro'
    };
    if (providerData.paymentMethod) {
      return methodMap[providerData.paymentMethod] || providerData.paymentMethod;
    }
    const termsMap: Record<string, string> = {
      immediate: 'Contado',
      net_15: 'Crédito 15 días',
      net_30: 'Crédito 30 días',
      net_60: 'Crédito 60 días',
      net_90: 'Crédito 90 días'
    };
    return termsMap[providerData.paymentTerms] || 'Sin datos';
  };

  const getTermLabel = (providerData: Provider) => {
    if (providerData.avgTermDays && Number(providerData.avgTermDays) > 0) {
      return `${Number(providerData.avgTermDays)} días`;
    }
    const termsMap: Record<string, string> = {
      immediate: '0 días',
      net_15: '15 días',
      net_30: '30 días',
      net_60: '60 días',
      net_90: '90 días'
    };
    return termsMap[providerData.paymentTerms] || 'Sin plazo';
  };

  if (!provider) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl rounded-3xl border-0 shadow-2xl p-0 overflow-hidden">
        <div className="p-6 pb-0">
          <DialogHeader className="text-left">
            <DialogTitle className="text-xl font-semibold text-left">{formattedName}</DialogTitle>
          </DialogHeader>
        </div>

        {/* Menubar para navegación */}
        <div className="px-6 pt-4 flex items-center justify-between gap-4">
          <Menubar className="border-0 bg-muted/30 rounded-lg p-1">
            <MenubarMenu>
              <MenubarTrigger
                className={`cursor-pointer rounded-md ${activeTab === 'overview' ? 'bg-background shadow-sm' : ''}`}
                onClick={() => setActiveTab('overview')}
              >
                Resumen
              </MenubarTrigger>
            </MenubarMenu>
            <MenubarMenu>
              <MenubarTrigger
                className={`cursor-pointer rounded-md ${activeTab === 'contact' ? 'bg-background shadow-sm' : ''}`}
                onClick={() => setActiveTab('contact')}
              >
                Contacto
              </MenubarTrigger>
            </MenubarMenu>
            <MenubarMenu>
              <MenubarTrigger
                className={`cursor-pointer rounded-md ${activeTab === 'ctacte' ? 'bg-background shadow-sm' : ''}`}
                onClick={() => setActiveTab('ctacte')}
              >
                CTA CTE
              </MenubarTrigger>
            </MenubarMenu>
            <MenubarMenu>
              <MenubarTrigger
                className={`cursor-pointer rounded-md ${activeTab === 'purchases' ? 'bg-background shadow-sm' : ''}`}
                onClick={() => setActiveTab('purchases')}
              >
                Compras
              </MenubarTrigger>
            </MenubarMenu>
          </Menubar>
          {onEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onEdit(provider)}
              className="gap-2"
            >
              <PencilIcon className="h-4 w-4" />
              Editar
            </Button>
          )}
        </div>

        <div className="px-6 pb-6 overflow-y-auto max-h-[calc(90vh-200px)] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 dark:[&::-webkit-scrollbar-thumb]:bg-gray-600 [&::-webkit-scrollbar-thumb]:rounded-full">
          {activeTab === 'overview' && (
            <div className="space-y-6 pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                    Balance actual
                  </h4>
                  <p className="text-2xl font-semibold text-foreground">
                    {formatCurrency(Number(provider.balanceCurrent || 0) + Number(provider.balanceOverdue || 0))}
                  </p>
                  <div className="text-xs text-muted-foreground mt-2 space-y-1">
                    {Number(provider.balanceOverdue || 0) > 0 && (
                      <div className="text-red-500">Vencido: {formatCurrency(provider.balanceOverdue || 0)}</div>
                    )}
                    {Number(provider.balanceCurrent || 0) > 0 && (
                      <div>Al dia: {formatCurrency(provider.balanceCurrent || 0)}</div>
                    )}
                    {Number(provider.balanceOverdue || 0) + Number(provider.balanceCurrent || 0) === 0 && (
                      <div>Sin saldo</div>
                    )}
                    {provider.nextDueDate && (
                      <div>Proximo vencimiento: {formatDate(provider.nextDueDate)}</div>
                    )}
                  </div>
                </div>

                <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                    Condiciones comerciales
                  </h4>
                  <p className="text-base font-medium">
                    {getPaymentMethodLabel(provider)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Plazo promedio: {getTermLabel(provider)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Beneficio: {provider.discountBenefit || 'Sin datos'}
                  </p>
                </div>

                <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                    Historial reciente
                  </h4>
                  <p className="text-base font-medium">
                    {provider.lastPurchaseDate ? formatDate(provider.lastPurchaseDate) : 'Sin compras'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Ultimo mes: {formatCurrency(provider.lastMonthTotal || 0)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Frecuencia: {provider.purchaseFrequency || 'Sin compras'}
                  </p>
                </div>

                <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                    Criticidad y preferencia
                  </h4>
                  <p className="text-base font-medium">
                    {getImportanceLabel(provider.importanceLevel)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Preferido: {provider.preferredAuto ? 'Auto' : provider.isPreferred ? 'Manual' : 'No'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Ultimo contacto: {formatDate(provider.lastContactDate)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Categorias: {categories.length > 0 ? categories.join(', ') : 'Sin categorias'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Especialidades: {specialties.length > 0 ? specialties.join(', ') : 'No registradas'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">'
                <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                    Notas internas
                  </h4>
                  <p className="text-sm leading-relaxed">
                    {provider.notes?.trim() ? provider.notes : 'No hay notas registradas para este proveedor.'}
                  </p>
                </div>

                <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                    Datos fiscales
                  </h4>
                  <dl className="text-sm space-y-2">
                    <div>
                      <dt className="text-muted-foreground">CUIT / Tax ID</dt>
                      <dd className="font-medium">{provider.taxId || 'Sin informar'}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Tipo</dt>
                      <dd className="font-medium">
                        {provider.type === 'company' ? 'Empresa' : 'Persona física'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Sitio web</dt>
                      <dd className="font-medium">
                        {provider.website ? (
                          <a
                            href={provider.website.startsWith('http') ? provider.website : `https://${provider.website}`}
                            className="text-foreground hover:underline"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {provider.website}
                          </a>
                        ) : (
                          'No especificado'
                        )}
                      </dd>
                    </div>
                  </dl>
                </div>

                <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                    Documentación
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    No hay documentos adjuntos. Podés cargar contratos, certificados o cualquier documentación relevante
                    desde la sección de archivos del proveedor.
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'contact' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-6">
              <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                  Contacto principal
                </h4>
                <dl className="space-y-2 text-sm">
                  <div>
                    <dt className="text-muted-foreground">Nombre</dt>
                    <dd className="font-medium">{provider.contactPerson || provider.name}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Cargo</dt>
                    <dd className="font-medium">{provider.contactPosition || 'No indicado'}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Email</dt>
                    <dd className="font-medium">{provider.contactEmail || provider.email || 'No informado'}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Teléfono / Celular</dt>
                    <dd className="font-medium">
                      {provider.contactPhone || provider.phone || provider.mobile || 'No informado'}
                    </dd>
                  </div>
                </dl>
              </div>

              <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                  Ubicación
                </h4>
                <dl className="space-y-2 text-sm">
                  <div>
                    <dt className="text-muted-foreground">Dirección</dt>
                    <dd className="font-medium">{provider.address || 'Sin dirección cargada'}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Ciudad</dt>
                    <dd className="font-medium">{provider.city || 'No informado'}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Provincia</dt>
                    <dd className="font-medium">{provider.state || 'No informado'}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">País</dt>
                    <dd className="font-medium">{provider.country || 'No informado'}</dd>
                  </div>
                </dl>
              </div>
            </div>
          )}

          {activeTab === 'ctacte' && (
            <div className="space-y-4 pt-6">
              <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div>
                    <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                      Cuenta corriente
                    </h4>
                    <p className="text-sm">
                      Saldo actual:{' '}
                      <span className="font-semibold text-lg">
                        {formatCurrency(movementsState.balance ?? provider.currentBalance)}
                      </span>
                    </p>
                    {movementsState.creditLimit !== null && movementsState.creditLimit !== undefined && (
                      <p className="text-xs text-muted-foreground">
                        Límite: {formatCurrency(movementsState.creditLimit)}
                      </p>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {movementsState.accountId
                      ? `Cuenta #${movementsState.accountId.slice(0, 8)}…`
                      : 'Cuenta corriente generada automáticamente'}
                  </div>
                </div>
              </div>

              {movementsState.loading && (
                <p className="text-sm text-muted-foreground">Cargando movimientos…</p>
              )}

              {!movementsState.loading && movementsState.error && (
                <p className="text-sm text-red-500">{movementsState.error}</p>
              )}

              {!movementsState.loading && !movementsState.error && movementsState.entries.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  Todavía no registraste movimientos con este proveedor.
                </p>
              )}

              {!movementsState.loading && !movementsState.error && movementsState.entries.length > 0 && (
                <div className="relative w-full overflow-x-auto rounded-xl border border-border">
                  <Table>
                    <TableHeader>
                      <TableRow className="hover:bg-muted/50 border-b">
                        <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">
                          Fecha
                        </TableHead>
                        <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">
                          Tipo
                        </TableHead>
                        <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">
                          Descripción
                        </TableHead>
                        <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-right">
                          Monto
                        </TableHead>
                        <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-right">
                          Saldo
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {movementsState.entries.map((movement) => (
                        <TableRow key={movement.id} className="border-b hover:bg-muted/50">
                          <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                            <div>{formatDate(movement.createdAt)}</div>
                          </TableCell>
                          <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                            <Badge
                              variant="secondary"
                              className={
                                movement.type === 'payment'
                                  ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                                  : movement.type === 'credit'
                                  ? 'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-300'
                                  : movement.type === 'charge'
                                  ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
                                  : movement.type === 'debit'
                                  ? 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300'
                                  : 'bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                              }
                            >
                              {MOVEMENT_TYPE_LABEL[movement.type] || movement.type}
                            </Badge>
                          </TableCell>
                          <TableCell className="p-2 align-middle text-left">
                            <div className="font-medium">{movement.description}</div>
                            {movement.reference && (
                              <div className="text-xs text-muted-foreground">Ref: {movement.reference}</div>
                            )}
                          </TableCell>
                          <TableCell className="p-2 align-middle whitespace-nowrap text-right">
                            <div className="font-medium">{formatCurrency(movement.amount)}</div>
                          </TableCell>
                          <TableCell className="p-2 align-middle whitespace-nowrap text-right">
                            <div className="font-medium">{formatCurrency(movement.newBalance)}</div>
                            <div className="text-xs text-muted-foreground">
                              Prev: {formatCurrency(movement.previousBalance)}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'purchases' && (
            <div className="pt-6">
              {purchasesState.loading && (
                <p className="text-sm text-muted-foreground">Cargando compras…</p>
              )}

              {!purchasesState.loading && purchasesState.error && (
                <p className="text-sm text-red-500">{purchasesState.error}</p>
              )}

              {!purchasesState.loading && !purchasesState.error && purchasesState.entries.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  No registraste compras con este proveedor todavía. Podés hacerlo desde la sección de Compras.
                </p>
              )}

              {!purchasesState.loading && !purchasesState.error && purchasesState.entries.length > 0 && (
                <div className="relative w-full overflow-x-auto rounded-xl border border-border">
                  <Table>
                    <TableHeader>
                      <TableRow className="hover:bg-muted/50 border-b">
                        <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">
                          N° Orden
                        </TableHead>
                        <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">
                          Fecha
                        </TableHead>
                        <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">
                          Estado
                        </TableHead>
                        <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-right">
                          Total
                        </TableHead>
                        <TableHead className="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {purchasesState.entries.map((purchase) => (
                        <TableRow key={purchase.id} className="border-b hover:bg-muted/50">
                          <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                            <div className="font-medium">
                              {purchase.purchaseNumber || `#${purchase.id.slice(0, 8)}`}
                            </div>
                            {purchase.items && purchase.items.length > 0 && (
                              <div className="text-xs text-muted-foreground">
                                {purchase.items.length} items
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                            <div>{formatDate(purchase.date)}</div>
                          </TableCell>
                          <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                            <Badge
                              variant="secondary"
                              className={
                                purchase.status === 'received'
                                  ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                                  : purchase.status === 'pending'
                                  ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                                  : 'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-300'
                              }
                            >
                              {purchase.status === 'received' ? 'Recibida' : purchase.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="p-2 align-middle whitespace-nowrap text-right">
                            <div className="font-medium">{formatCurrency(purchase.total ?? 0)}</div>
                          </TableCell>
                          <TableCell className="p-2 align-middle whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-end">
                              <ButtonGroup>
                                <Tip label="Ver detalles">
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedPurchase(purchase);
                                    }}
                                    className="size-8"
                                  >
                                    <EyeIcon className="h-4 w-4" />
                                  </Button>
                                </Tip>
                              </ButtonGroup>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>

      {/* Modal de detalles de compra */}
      {selectedPurchase && (
        <Dialog open={!!selectedPurchase} onOpenChange={() => setSelectedPurchase(null)}>
          <DialogContent className="max-w-3xl rounded-3xl border-0 shadow-2xl p-0 overflow-hidden">
            <div className="p-6 pb-0">
              <DialogHeader>
                <DialogTitle className="text-xl font-semibold">
                  Detalles de Compra #{selectedPurchase.purchaseNumber || selectedPurchase.id.slice(0, 8)}
                </DialogTitle>
              </DialogHeader>
            </div>

            <div className="px-6 pb-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <div className="space-y-4 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Fecha</p>
                    <p className="font-medium">{formatDate(selectedPurchase.date)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Estado</p>
                    <div className="mt-1">
                      <Badge
                        variant="secondary"
                        className={
                          selectedPurchase.status === 'received'
                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                            : selectedPurchase.status === 'pending'
                            ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                            : 'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-300'
                        }
                      >
                        {selectedPurchase.status === 'received' ? 'Recibida' : selectedPurchase.status}
                      </Badge>
                    </div>
                  </div>
                </div>

                {selectedPurchase.items && selectedPurchase.items.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground font-medium">Items</p>
                    <div className="relative w-full overflow-x-auto rounded-xl border border-border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Descripción</TableHead>
                            <TableHead className="text-right">Cantidad</TableHead>
                            <TableHead className="text-right">Precio Unit.</TableHead>
                            <TableHead className="text-right">Total</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {selectedPurchase.items.map((item, index) => (
                            <TableRow key={index}>
                              <TableCell>{item.name}</TableCell>
                              <TableCell className="text-right">{item.qty}</TableCell>
                              <TableCell className="text-right">{formatCurrency(item.unitCost)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(item.totalCost || item.qty * item.unitCost)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                <div className="rounded-lg bg-muted/30 p-4 space-y-2">
                  <div className="flex justify-between">
                    <span>Subtotal:</span>
                    <span className="font-medium">{formatCurrency(selectedPurchase.subtotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>IVA:</span>
                    <span className="font-medium">{formatCurrency(selectedPurchase.tax)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t pt-2 border-border">
                    <span>Total:</span>
                    <span>{formatCurrency(selectedPurchase.total)}</span>
                  </div>
                </div>

                {selectedPurchase.notes && (
                  <div>
                    <p className="text-sm text-muted-foreground font-medium">Notas</p>
                    <p className="mt-1 text-sm">{selectedPurchase.notes}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="px-6 py-4 bg-muted/30 border-t flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setSelectedPurchase(null)}
              >
                Cerrar
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
};

export default ProviderDetailModal;

